import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import {
  COLORS,
  FALLBACK_COMMODITIES,
  FALLBACK_COUNTRIES,
  FALLBACK_ENERGY,
  FALLBACK_MAP_EVENTS,
  FALLBACK_MARKETS,
  FALLBACK_NEWS,
  FALLBACK_REGIONS,
  FETCH_STORAGE_KEY,
  LAYERS,
  NEWS_CHANNELS,
  TABS,
} from "./world-monitor/worldMonitorData";
import { Badge } from "./world-monitor/worldMonitorUi";

const WorldMonitorMapTab = lazy(() => import("./world-monitor/WorldMonitorMap"));
const WorldMonitorVideosTab = lazy(() => import("./world-monitor/WorldMonitorVideos"));
const WorldMonitorIntelTab = lazy(() =>
  import("./world-monitor/WorldMonitorPanels").then(module => ({ default: module.WorldMonitorIntelTab }))
);
const WorldMonitorMarketsTab = lazy(() =>
  import("./world-monitor/WorldMonitorPanels").then(module => ({ default: module.WorldMonitorMarketsTab }))
);
const WorldMonitorRegionsTab = lazy(() =>
  import("./world-monitor/WorldMonitorPanels").then(module => ({ default: module.WorldMonitorRegionsTab }))
);

function TopBar({ activeTab, setActiveTab, fetchEnabled, onToggleFetch, liveState, updatedAt }) {
  return (
    <div className="sticky top-0 z-40 border-b" style={{ background: COLORS.top, borderColor: COLORS.border }}>
      <div className="flex min-h-[72px] items-center gap-3 overflow-x-auto px-3">
        <div className="font-mono text-2xl font-black tracking-[0.25em]" style={{ color: "#fff" }}>
          MONITOR
        </div>
        <div className="font-mono text-xs font-black" style={{ color: COLORS.muted }}>v2.8.0</div>
        <div className="flex items-center gap-2 font-mono text-sm" style={{ color: fetchEnabled ? COLORS.text : COLORS.red }}>
          <span className="h-3 w-3 rounded-full" style={{ background: fetchEnabled ? COLORS.green : COLORS.red, boxShadow: `0 0 12px ${fetchEnabled ? COLORS.green : COLORS.red}` }} />
          {fetchEnabled ? "LIVE" : "STOPPED"}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleFetch}
            className="h-10 min-w-[118px] rounded border px-4 font-mono text-sm font-black"
            style={{
              background: fetchEnabled ? "rgba(255,51,51,0.18)" : "rgba(0,217,120,0.16)",
              borderColor: fetchEnabled ? COLORS.red : COLORS.green,
              color: fetchEnabled ? COLORS.red : COLORS.green,
            }}
          >
            {fetchEnabled ? "STOP DATA" : "START DATA"}
          </button>
          <Badge tone={liveState.status === "live" ? "green" : liveState.status === "stopped" ? "red" : "amber"}>
            {liveState.status}
          </Badge>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto border-t px-3 py-3" style={{ borderColor: COLORS.borderSoft }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className="rounded border px-4 py-2 font-mono text-xs font-black uppercase tracking-widest"
            style={{
              borderColor: activeTab === tab.id ? COLORS.green : COLORS.border,
              background: activeTab === tab.id ? "rgba(0,217,120,0.12)" : COLORS.panel,
              color: activeTab === tab.id ? COLORS.green : COLORS.text2,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="border-t px-4 py-2 font-mono text-xs" style={{ borderColor: COLORS.borderSoft, background: COLORS.bg2, color: COLORS.text2 }}>
        {liveState.message}
        {updatedAt && <span> - UPDATED {new Date(updatedAt).toLocaleTimeString()}</span>}
      </div>
    </div>
  );
}

function LoadingTab() {
  return (
    <div className="flex min-h-[420px] items-center justify-center border font-mono text-lg font-black" style={{ borderColor: COLORS.border, color: COLORS.yellow }}>
      LOADING WORLD MONITOR TAB...
    </div>
  );
}

class WorldMonitorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("World Monitor tab crashed", error, info);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="border p-5 font-mono" style={{ borderColor: COLORS.red, background: COLORS.panel, color: COLORS.text }}>
        <div className="text-lg font-black" style={{ color: COLORS.red }}>WORLD MONITOR TAB ERROR</div>
        <div className="mt-3 text-sm leading-6" style={{ color: COLORS.text2 }}>
          {this.state.error?.message || "A World Monitor tab failed to render."}
        </div>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="mt-4 rounded border px-4 py-2 text-xs font-black"
          style={{ borderColor: COLORS.yellow, color: COLORS.yellow }}
        >
          RETRY TAB
        </button>
      </div>
    );
  }
}

export default function WorldMonitorClone() {
  const [activeTab, setActiveTab] = useState("map");
  const [activeLayers, setActiveLayers] = useState(() => LAYERS.filter(layer => layer.checked).map(layer => layer.id));
  const [liveData, setLiveData] = useState(null);
  const [fetchEnabled, setFetchEnabled] = useState(() => localStorage.getItem(FETCH_STORAGE_KEY) === "start");
  const [liveState, setLiveState] = useState(() => (
    localStorage.getItem(FETCH_STORAGE_KEY) === "start"
      ? { status: "loading", message: "Fetching live public feeds..." }
      : { status: "stopped", message: "Page data fetch is stopped. Click START DATA to load live sources." }
  ));

  useEffect(() => {
    let active = true;
    let timer;
    const controller = new AbortController();

    if (!fetchEnabled) {
      setLiveState({ status: "stopped", message: "Page data fetch is stopped. Click START DATA to load live sources." });
      return () => {
        active = false;
        controller.abort();
      };
    }

    const loadLiveData = async () => {
      try {
        const response = await fetch("/api/world-monitor/live", { cache: "no-store", signal: controller.signal });
        const payload = await response.json();
        if (!active) return;
        if (!response.ok || !payload.success) throw new Error(payload.error || "Live snapshot failed");

        setLiveData(payload);
        setLiveState({
          status: payload.errors?.length ? "partial" : "live",
          message: payload.errors?.length
            ? `Live with ${payload.errors.length} source warning(s)`
            : `Live from ${payload.sources?.length || 0} public source groups`,
        });
      } catch (error) {
        if (!active || error.name === "AbortError") return;
        setLiveState({
          status: "fallback",
          message: error.message || "Using fallback rows until backend live feed responds",
        });
      }
    };

    loadLiveData();
    timer = setInterval(loadLiveData, 90000);

    return () => {
      active = false;
      controller.abort();
      clearInterval(timer);
    };
  }, [fetchEnabled]);

  const pageData = useMemo(() => ({
    mapEvents: liveData?.mapEvents?.length ? liveData.mapEvents : FALLBACK_MAP_EVENTS,
    liveNews: liveData?.liveNews?.length ? liveData.liveNews : FALLBACK_NEWS,
    worldNews: liveData?.worldNews?.length ? liveData.worldNews : FALLBACK_NEWS,
    regions: liveData?.regionNews?.length ? liveData.regionNews : FALLBACK_REGIONS,
    countryRows: liveData?.countryInstability?.length ? liveData.countryInstability : FALLBACK_COUNTRIES,
    forecasts: liveData?.aiForecasts || [],
    posture: liveData?.strategicPosture || [],
    markets: liveData?.markets?.length ? liveData.markets : FALLBACK_MARKETS,
    commodities: liveData?.commodities?.length ? liveData.commodities : FALLBACK_COMMODITIES,
    energy: liveData?.energyComplex?.length ? liveData.energyComplex : FALLBACK_ENERGY,
    videos: liveData?.videos?.length ? liveData.videos : NEWS_CHANNELS,
    insight: liveData?.aiInsights,
  }), [liveData]);

  const toggleFetch = () => {
    setFetchEnabled(prev => {
      const next = !prev;
      localStorage.setItem(FETCH_STORAGE_KEY, next ? "start" : "stop");
      if (!next) {
        setLiveData(null);
        setLiveState({ status: "stopped", message: "Page data fetch is stopped. Click START DATA to load live sources." });
      } else {
        setLiveState({ status: "loading", message: "Fetching live public feeds..." });
      }
      return next;
    });
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, color: COLORS.text }}>
      <TopBar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        fetchEnabled={fetchEnabled}
        onToggleFetch={toggleFetch}
        liveState={liveState}
        updatedAt={liveData?.updatedAt}
      />

      <main className="p-2">
        <WorldMonitorErrorBoundary resetKey={activeTab}>
          <Suspense fallback={<LoadingTab />}>
            {activeTab === "map" && (
              <WorldMonitorMapTab
                activeLayers={activeLayers}
                setActiveLayers={setActiveLayers}
                mapEvents={pageData.mapEvents}
                fetchEnabled={fetchEnabled}
              />
            )}

            {activeTab === "videos" && (
              <WorldMonitorVideosTab
                liveNews={pageData.liveNews}
                videos={pageData.videos}
                fetchEnabled={fetchEnabled}
              />
            )}

            {activeTab === "intel" && (
              <WorldMonitorIntelTab
                liveNews={pageData.worldNews}
                countryRows={pageData.countryRows}
                forecasts={pageData.forecasts}
                posture={pageData.posture}
                insight={pageData.insight}
              />
            )}

            {activeTab === "markets" && (
              <WorldMonitorMarketsTab
                markets={pageData.markets}
                commodities={pageData.commodities}
                energy={pageData.energy}
              />
            )}

            {activeTab === "regions" && (
              <WorldMonitorRegionsTab regions={pageData.regions} />
            )}
          </Suspense>
        </WorldMonitorErrorBoundary>
      </main>
    </div>
  );
}
